import { IsBoolean, IsOptional, IsString, Matches, MaxLength } from 'class-validator';

/**
 * A New Brunswick Parcel Identifier: eight digits, as Service New Brunswick
 * prints it. Intake used to take 300 characters of anything here.
 */
export const PID_PATTERN = /^\d{8}$/;

/**
 * A Property Account Number: digits only. SNB's are six to eight digits in
 * practice; the bounds match what record extraction already accepts.
 */
export const PAN_PATTERN = /^\d{4,12}$/;

/** The same, or blank (the PAN is optional at intake). */
export const OPTIONAL_PAN_PATTERN = /^(\d{4,12})?$/;

/**
 * A parcel (PID) linked to a file. A property may span more than one PID under a
 * single PAN (Stage 1 tombstone spec §4), so parcels are a one-to-many list on
 * the file — every downstream document pulls the full list, not just the primary.
 * The detail fields mirror the SNB Parcel Information record.
 */
export interface ParcelDetail {
  id: string;
  pid: string;
  isPrimary: boolean;
  sortOrder: number;
  county: string | null;
  area: string | null;
  areaUnit: string | null;
  landTitlesStatus: string | null;
  landGazette: string | null;
  interestType: string | null;
  civicNumber: string | null;
  streetName: string | null;
  streetType: string | null;
  streetDirection: string | null;
  placeName: string | null;
  parish: string | null;
}

/**
 * A parcel as submitted at intake or from the tombstone editor. `id` echoes an
 * existing parcel on update (reconcile-by-id); omit it for a new parcel. Only
 * `pid` is required — the SNB detail fields are all optional.
 */
export class ParcelInputDto {
  @IsOptional()
  @IsString()
  id?: string;

  @IsString()
  @MaxLength(32)
  @Matches(PID_PATTERN, { message: 'pid must be an 8-digit Parcel Identifier' })
  pid!: string;

  @IsOptional()
  @IsBoolean()
  isPrimary?: boolean;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  county?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  area?: string;

  @IsOptional()
  @IsString()
  @MaxLength(48)
  areaUnit?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  landTitlesStatus?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  landGazette?: string;

  @IsOptional()
  @IsString()
  @MaxLength(64)
  interestType?: string;

  @IsOptional()
  @IsString()
  @MaxLength(32)
  civicNumber?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  streetName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(48)
  streetType?: string;

  @IsOptional()
  @IsString()
  @MaxLength(8)
  streetDirection?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  placeName?: string;

  @IsOptional()
  @IsString()
  @MaxLength(120)
  parish?: string;
}

/**
 * The detail fields the SNB Parcel record supplies, in display order.
 *
 * Exported so the intake form, the tombstone editor and anything else that
 * renders or copies parcel detail iterate one list. Two screens keeping their
 * own copies is how the override log came to omit three fields.
 */
export const PARCEL_DETAIL_KEYS = [
  'county',
  'area',
  'areaUnit',
  'landTitlesStatus',
  'landGazette',
  'interestType',
  'civicNumber',
  'streetName',
  'streetType',
  'streetDirection',
  'placeName',
  'parish',
] as const satisfies readonly (keyof ParcelInputDto)[];

export type ParcelDetailKey = (typeof PARCEL_DETAIL_KEYS)[number];

/** A parcel's detail fields, minus identity and any blanks. */
export function parcelDetailOf(parcel: ParcelInputDto): Partial<ParcelInputDto> {
  const out: Partial<ParcelInputDto> = {};
  for (const key of PARCEL_DETAIL_KEYS) {
    const value = parcel[key];
    // Skip blanks so a freshly read record never erases a value already held.
    if (value != null && value !== '') out[key] = value;
  }
  return out;
}

/** The fields every parcel-row shape shares, web form, mobile form or DTO. */
type ParcelRowLike = { pid: string; isPrimary?: boolean } & Partial<
  Record<ParcelDetailKey, string>
>;

/**
 * Add the PIDs the SNB records list that the form does not have yet.
 *
 * With no PID on the form the records' list is taken whole. Otherwise every
 * row is kept and each missing PID is appended, never as primary: a PID typed
 * by hand must not stop the second lot of a straddled property from reaching
 * the file — and every notice that names "the property".
 */
export function addRecordPids<T extends ParcelRowLike>(rows: T[], fromRecords: T[]): T[] {
  const typed = rows.filter((r) => r.pid.trim() !== '');
  if (typed.length === 0) return fromRecords;
  const have = new Set(typed.map((r) => r.pid.trim()));
  const added = fromRecords
    .filter((r) => r.pid.trim() !== '' && !have.has(r.pid.trim()))
    .map((r) => ({ ...r, isPrimary: false }));
  return [...rows, ...added];
}

/**
 * {@link addRecordPids}, after filling the rows already listed from the
 * matching record. Fill blanks only: anything already on a row was entered by a
 * person, or read from an earlier record, and a newly read document must not
 * overwrite it.
 */
export function mergeRecordParcels<T extends ParcelRowLike>(rows: T[], fromRecords: T[]): T[] {
  const filled = rows.map((row) => {
    const match = fromRecords.find((r) => r.pid.trim() !== '' && r.pid.trim() === row.pid.trim());
    if (!match) return row;
    const out = { ...row };
    for (const key of PARCEL_DETAIL_KEYS) {
      const value = match[key];
      if ((out[key] == null || out[key] === '') && value != null && value !== '') {
        (out as ParcelRowLike)[key] = value;
      }
    }
    return out;
  });
  return addRecordPids(filled, fromRecords);
}
